import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || '';

const getHeaders = () => authService.getAuthHeaders();

interface AccessRequest {
  id: number;
  lab_id: string;
  lab_name?: string | null;
  kind: string;
  status: string;
  target_user_id: number;
  target_email: string;
  target_name?: string | null;
  requester_email?: string | null;
  reason?: string | null;
  resolution_note?: string | null;
  created_at: string;
  resolved_at?: string | null;
}

const statusColor = (s: string): any => {
  switch (s) {
    case 'open': return 'warning';
    case 'approved': return 'success';
    case 'declined': return 'error';
    case 'cancelled': return 'default';
    default: return 'default';
  }
};

const apiError = (e: any, fallback: string) =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || fallback;

const AccessRequests: React.FC = () => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // One dialog for both answers — declining REQUIRES a note (the API enforces
  // it too), approving takes an optional one.
  const [resolving, setResolving] = useState<{ req: AccessRequest; approve: boolean } | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/v1/lis-portal/access-requests`, {
        headers: getHeaders(),
        params: { status: statusFilter },
      });
      setRequests(res.data || []);
    } catch (e: any) {
      setError(apiError(e, 'Failed to load access requests'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async () => {
    if (!resolving) return;
    const { req, approve } = resolving;
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/v1/lis-portal/access-requests/${req.id}/${approve ? 'approve' : 'decline'}`,
        { note: note || null },
        { headers: getHeaders(), params: { lab_id: req.lab_id } },
      );
      setNotice(
        approve
          ? `Revoked ${req.target_email}'s access to ${req.lab_name || req.lab_id}.`
          : `Declined the request for ${req.target_email}. The lab sees your reason.`,
      );
      setResolving(null);
      setNote('');
      await load();
    } catch (e: any) {
      setError(apiError(e, 'Failed to resolve that request'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const openCount = requests.filter((r) => r.status === 'open').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" gutterBottom>Access requests</Typography>
          <Typography variant="body2" color="text.secondary">
            Labs can't remove your staff themselves — their logins are yours. When a lab
            wants someone off their data, the request lands here and you decide.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="ar-status">Show</InputLabel>
          <Select
            labelId="ar-status" label="Show" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as string)}
          >
            <MenuItem value="open">Open{openCount ? ` (${openCount})` : ''}</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="declined">Declined</MenuItem>
            <MenuItem value="cancelled">Withdrawn</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ my: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Paper sx={{ mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lab</TableCell>
                <TableCell>Person</TableCell>
                <TableCell>Asked by</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Raised</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nothing here.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {requests.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.lab_name || r.lab_id}</TableCell>
                  <TableCell>{r.target_email}</TableCell>
                  <TableCell>{r.requester_email || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>{r.reason || '—'}</TableCell>
                  <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Chip size="small" color={statusColor(r.status)} label={r.status} /></TableCell>
                  <TableCell align="right">
                    {r.status === 'open' ? (
                      <>
                        <Button
                          size="small" color="error" startIcon={<CheckIcon />} disabled={busy}
                          onClick={() => { setResolving({ req: r, approve: true }); setNote(''); }}
                        >
                          Revoke
                        </Button>
                        <Button
                          size="small" startIcon={<CloseIcon />} disabled={busy} sx={{ ml: 1 }}
                          onClick={() => { setResolving({ req: r, approve: false }); setNote(''); }}
                        >
                          Decline
                        </Button>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {r.resolution_note || '—'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!resolving} onClose={() => setResolving(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resolving?.approve
            ? `Revoke ${resolving?.req.target_email}'s access?`
            : `Decline the request for ${resolving?.req.target_email}?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {resolving?.approve
              ? `They lose access to ${resolving?.req.lab_name || resolving?.req.lab_id} immediately. Your other labs and their account are untouched.`
              : 'Nothing is revoked. The lab sees your reason on their team page, so make it something they can act on.'}
          </Typography>
          <TextField
            fullWidth multiline rows={3} margin="dense" autoFocus
            label={resolving?.approve ? 'Note (optional)' : 'Reason (required)'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolving(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={resolving?.approve ? 'error' : 'primary'}
            onClick={handleResolve}
            disabled={busy || (!resolving?.approve && !note.trim())}
          >
            {resolving?.approve ? 'Revoke access' : 'Send decline'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessRequests;
